/**
 * Workbox service worker registration and update handling.
 * Uses workbox-window for proper update flow with messageSkipWaiting().
 */

import { Workbox } from "workbox-window";

let wb: Workbox | null = null;

/**
 * Initialize Workbox and register the service worker.
 * Call this once when the app loads (e.g. in index.tsx).
 */
export function registerWorkbox(): void {
  if (!("serviceWorker" in navigator)) return;

  wb = new Workbox("/service-worker.js");

  // Reload when the new service worker takes control
  wb.addEventListener("controlling", () => {
    window.location.reload();
  });

  wb.register().catch((err) => {
    console.error("Service worker registration failed:", err);
  });
}

/**
 * Triggers a service worker update check and applies any available update.
 * Uses Workbox's messageSkipWaiting() for reliable update activation.
 * When an update is found and applied, the page will reload.
 */
export async function triggerServiceWorkerUpdate(): Promise<{
  success: boolean;
  message: string;
  reloaded?: boolean;
}> {
  if (!("serviceWorker" in navigator)) {
    return {
      success: false,
      message: "Service Worker is not supported in this browser",
    };
  }

  if (!wb) {
    return {
      success: false,
      message: "Service worker not yet registered",
    };
  }

  try {
    const registration = await navigator.serviceWorker.getRegistration();

    if (!registration) {
      return {
        success: false,
        message: "No service worker registration found",
      };
    }

    // Check for an already waiting service worker (update was found previously)
    const waitingWorker: ServiceWorker | null = registration.waiting;
    if (waitingWorker) {
      wb.messageSkipWaiting();
      return {
        success: true,
        message: "Update found. Reloading...",
        reloaded: false,
      };
    }

    // Set up one-time listener for when a new service worker is waiting
    const handleWaiting = () => {
      wb?.removeEventListener("waiting", handleWaiting);
      wb?.messageSkipWaiting();
    };
    wb.addEventListener("waiting", handleWaiting);

    // Trigger the update check
    await wb.update();

    // If no update was found immediately, the SW might still be checking
    await new Promise((resolve) => setTimeout(resolve, 1000));

    const waitingWorkerAfterUpdate: ServiceWorker | null = registration.waiting;
    if (waitingWorkerAfterUpdate) {
      wb.messageSkipWaiting();
      return {
        success: true,
        message: "Update found. Reloading...",
        reloaded: false,
      };
    }

    return {
      success: true,
      message: "بررسی به‌روزرسانی انجام شد. اپلیکیشن شما به‌روز است.",
    };
  } catch (error) {
    console.error("Service worker update error:", error);
    return {
      success: false,
      message: "خطا در به‌روزرسانی. لطفاً صفحه را رفرش کنید.",
    };
  }
}
